<template>
  <div
    class="field"
    :class="[
      hasValue({
        id: field.id,
        category: field.category || category,
      }) && 'has-value',
      bulkDelete ? 'bulk-delete' : 'bulk-edit',
    ]"
    v-if="
      (field.type === 'group' && isGroupVisible(field)) ||
      bulkDelete ||
      (configVisibility['bulkEdit'] &&
        configVisibility['bulkEdit'][field.category || category] &&
        configVisibility['bulkEdit'][field.category || category][field.id])
    "
  >
    <div class="question" :id="field.id">
      <div v-if="isChecklist(field)" class="checklist">
        <p v-if="checklistLabel(field)">
          {{ checklistLabel(field) }}
        </p>
        <!-- Need special handling for checklists as each option in the group needs it's own checkmark when deleting -->
        <div
          v-for="groupField in checklistItems(field)"
          :key="getFieldData(groupField.id)['FieldId']"
        >
          <bulkDeleteCheckbox
            :label="getFieldValue('label', groupField.id)"
            :disabled="getFieldValue('disabled', groupField.id)"
            :fields="[
              {
                id: groupField.id,
                category: groupField.category || category,
                parentField: groupField.parentField || parentField,
                type:
                  getFieldType({
                    id: groupField.id,
                    category: groupField.category || category,
                  }) || '',
              },
            ]"
          />
        </div>
      </div>
      <template v-else>
        <bulkDeleteCheckbox
          v-if="bulkDelete"
          :fields="[
            {
              id: field.id,
              category: field.category || category,
              parentField: field.parentField || parentField,
              type:
                getFieldType({
                  id: field.id,
                  category: field.category || category,
                }) || '',
            },
          ]"
        />
        <p>
          {{ getFieldValue("label", field.id) || field.label || field.id }}
          <tooltip :text="getFieldValue('tooltip', field.id)" />
          <helpIcon
            v-if="getFieldValue('helpContent', field.id)"
            :content="getFieldValue('helpContent', field.id)"
          />
        </p>
      </template>
    </div>
    <div class="answer" v-if="!bulkDelete">
      <group
        v-if="field.type === 'group'"
        :category="field.category || category"
        :field="field"
        :parentField="parentField"
        :id="bulkDelete ? 'bulkDelete' : 'bulkEdit'"
      />
      <contentGroup
        class="body-2 content-group"
        v-else-if="field.type === 'contentGroup'"
        :category="field.category || category"
        :field="field"
        :parentField="parentField"
        :id="bulkDelete ? 'bulkDelete' : 'bulkEdit'"
      />
      <configField
        v-else
        :category="field.category || category"
        :field="
          getFieldData({
            id: field.id,
            category: field.category || category,
          })
        "
        :value="
          getConfigValue({
            id: field.id,
            category: field.category || category,
          })
        "
        :layout="field.layout"
        :parentField="parentField"
        :id="bulkDelete ? 'bulkDelete' : 'bulkEdit'"
        hideLabel
        hideTooltip
        @setConfigValue="setConfigValue"
        @unsetConfigValue="unsetConfigValue"
        emitSetConfig
      />
    </div>
  </div>
</template>

<script>
/**
 * @fileoverview Config field
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { mapGetters } from "vuex";

import configField from "@/components/config-field";
import tooltip from "@/components/tooltip";
import helpIcon from "@/components/help-icon";

import bulkDeleteCheckbox from "./bulk-delete-checkbox";
import contentGroup from "./content-group";
import group from "./group";

import getFieldData from "@/util/getFieldData";

import fieldMap from "@/components/config-field/field-map.json";

export default {
  name: "ConfigField",
  components: {
    bulkDeleteCheckbox,
    configField,
    contentGroup,
    group,
    tooltip,
    helpIcon,
  },
  props: {
    field: {
      type: Object,
    },
    category: {
      type: String,
    },
    parentField: {
      type: String,
    },
    bulkDelete: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      updatedValues: "getUpdatedConfigValues",
      configTemplate: "getConfigTemplate",
      configVisibility: "getConfigVisibility",
    }),
  },
  methods: {
    isChecklist(field) {
      return this.bulkDelete && field.groupType === "checklist";
    },
    checklistLabel(field) {
      if (field.label) return field.label;
      return this.getFieldValue("label", field.id);
    },
    checklistItems(field) {
      if (field.items) return field.items;
      if (field.layout)
        return field.layout.map((item) => ({
          id: item[0].id,
          parentField: field.id,
        }));
      return [];
    },
    isGroupVisible(field) {
      if (this.bulkDelete) return true;
      return !!field.items.find(
        (item) =>
          this.configVisibility["bulkEdit"][item.category || this.category][
            item.id
          ]
      );
    },
    fieldList(category = this.category) {
      const fieldData = this.configTemplate && this.configTemplate[category];
      return fieldData && fieldData.AvailableFieldList;
    },
    hasValue({ id, category }) {
      return typeof this.getConfigValue({ id, category }) !== "undefined";
    },
    getConfigValue({ id, category = this.category }) {
      const clientId = this.bulkDelete ? "bulkDelete" : "bulkEdit";

      let configValue =
        this.updatedValues[clientId] && this.updatedValues[clientId][category];

      if (this.parentField !== undefined) {
        configValue =
          configValue &&
          this.updatedValues[clientId][category][this.parentField] &&
          this.updatedValues[clientId][category][this.parentField][id];
      } else {
        configValue = configValue && this.updatedValues[clientId][category][id];
      }
      return configValue;
    },
    getFieldData({ id, category }) {
      return getFieldData(this.fieldList(category), id);
    },
    getFieldType({ id, category }) {
      const fieldData = this.getFieldData({ id, category });
      if (fieldData && fieldData.Type) return fieldData.Type;
      return null;
    },
    getFieldValue(value, id) {
      let returnValue;
      if (value !== undefined && value.length > 0) {
        const fieldData = getFieldData(this.fieldList(this.category), id);
        if (fieldData) {
          let configKey = value.charAt(0).toUpperCase() + value.slice(1);
          returnValue = fieldData[configKey];
        }
      }
      if (returnValue === undefined) {
        returnValue =
          fieldMap[this.category] &&
          fieldMap[this.category][id] &&
          fieldMap[this.category][id][value];
      }
      return returnValue;
    },
    setConfigValue(args) {
      let { value, fieldId, category } = args;
      const fieldData = this.getFieldData({ id: fieldId, category });

      // need to allow fully unsetting values
      if (fieldData.Type === "checkbox" && value === false) value = undefined;
      if (value === "") value = undefined;

      this.$store.dispatch("setConfigValue", {
        ClientInfoRuleId: this.bulkDelete ? "bulkDelete" : "bulkEdit",
        value,
        category,
        fieldId,
      });
    },
    unsetConfigValue(args) {
      let { value, fieldId, category } = args;

      this.$store.dispatch("unsetConfigValue", {
        ClientInfoRuleId: this.bulkDelete ? "bulkDelete" : "bulkEdit",
        value,
        category,
        fieldId,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.field {
  display: grid;
  align-items: flex-start;
  grid-template-columns: 1fr 1fr;
  min-height: 81px;
  gap: 36px;
  &.bulk-delete {
    align-items: center;
  }

  .question {
    display: flex;
    align-items: flex-start;
    gap: $qvo-spacing-8;
    padding: 20px 0 20px $qvo-spacing-16;
    &::v-deep {
      .v-icon {
        top: -1px;
      }
    }
  }
  .answer {
    padding: 20px $qvo-spacing-16 20px 0;
  }
  &::v-deep {
    .config-field {
      margin-bottom: 0;
    }
  }
}
.content-group {
  padding: $qvo-spacing-16 $qvo-spacing-16;
  margin-bottom: $qvo-spacing-24;
  border-radius: 8px;
  background: $qvo-color-blue-100;
  &::v-deep {
    .config-field {
      margin-bottom: 0;
    }
  }
}
.content-field {
  margin-bottom: $qvo-spacing-24;
}
.has-value {
  border-left: 4px solid $qvo-color-blue-400;
  background: $qvo-color-blue-100;
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
  .question {
    margin-left: -4px;
  }
  .answer {
    margin-left: -2px;
  }
}

.checklist {
  display: flex;
  flex-direction: column;
  gap: $qvo-spacing-16;
}
</style>